import React, {
  InputHTMLAttributes,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import { useField } from '@unform/core';

import { ReactComponent as EyeClosed } from '../../../assets/icons/ubr_eye_closed.svg';
import { ReactComponent as Eye } from '../../../assets/icons/ubr_eye.svg';
import {
  coop,
  beneficiario,
  posto,
  data,
  conta,
  agencia,
  identificadorParceiro,
} from '../../../utils/masks';
import { ToggleView, ErrorMessage, InputContainer, InputGroup } from './styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  mask?: 'coop' | 'beneficiario' | 'posto' | 'data' | 'conta' | 'agencia'| "identificadorParceiro";
  label: string;
  name: string;
  loading?: boolean;
  isDisabled?: boolean;
  disableMargin?: boolean;
}

export const Input: React.FC<InputProps> = ({
  mask,
  label,
  type,
  name,
  loading,
  isDisabled,
  defaultValue: propDefaultValue,
  disableMargin,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isFilled, setIsFilled] = useState<boolean>(false);

  const {
    fieldName,
    error,
    defaultValue,
    registerField,
    clearError,
  } = useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
    setIsFilled(!!inputRef.current?.value);
  }, [fieldName, registerField]);

  const handleHoverInput = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
    clearError();
  }, []);

  const handleStopHoveringInput = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);

    setIsFilled(!!inputRef.current?.value);
  }, []);

  const handleToggleView = useCallback(() => {
    setIsVisible(state => !state);
  }, []);

  const handleKeyUp = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (mask === 'coop') {
        coop(e);
      }
      if (mask === 'beneficiario') {
        beneficiario(e);
      }
      if (mask === 'posto') {
        posto(e);
      }

      if (mask === 'data') {
        data(e);
      }

      if (mask === 'conta') {
        conta(e);
      }

      if (mask === 'agencia') {
        agencia(e);
      }

      if(mask === 'identificadorParceiro'){
        identificadorParceiro(e);
      }
    },
    [mask],
  );

  return (
    <>
      <InputContainer disableMargin={disableMargin}>
        <label>{label}</label>
        <InputGroup
          onMouseOver={handleHoverInput}
          onMouseLeave={handleStopHoveringInput}
          type={type}
          isFilled={isFilled}
          isFocused={isFocused}
          hasError={!!error}
          hasValueInProps={!!propDefaultValue}
          isDisabled={isDisabled}
          loading={loading || isDisabled}
          data-testid="input-container"
        >
          <input
            id={name}
            ref={inputRef}
            defaultValue={propDefaultValue || defaultValue}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyUp={handleKeyUp}
            autoComplete="off"
            placeholder={loading ? 'Carregando...' : ''}
            disabled={loading || isDisabled}
            name={name}
            type={
              type === 'password'
                ? isVisible
                  ? 'text'
                  : 'password'
                : type || 'text'
            }
            {...rest}
          />
          {type === 'password' && (
            <ToggleView type="button" onClick={handleToggleView}>
              {isVisible ? <EyeClosed width={24} /> : <Eye width={24} />}
            </ToggleView>
          )}
        </InputGroup>
        <ErrorMessage hasError={!!error} isHovered={isHovered}>
          <div>{error}</div>
        </ErrorMessage>
      </InputContainer>
    </>
  );
};
